import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ConfigService } from '../../shared-services/config.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'lib-popup-table',
  imports: [
    CommonModule,
    NgxDatatableModule,
    MatDialogModule,
    CommonModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  templateUrl: './popup-table.component.html',
  styleUrl: './popup-table.component.scss',
})
export class PopupTableComponent {
  grandTotal: number = 0;
  columnMode: ColumnMode = ColumnMode.force;
  isAuditHistoryLayout = false;
  rowHeight: number | 'auto' = 'auto';
  projectTitle = '';
  rows: any;
  pageSize: number | undefined;
  private unSubscribe$: Subject<void> = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService
  ) {}
  isLoading = false;
  private readonly auditMetaKeys = new Set([
    'uuid',
    'record_id',
    'version_no',
    'is_current',
    'created_by',
    'created_date',
    'updated_by',
    'updated_date',
  ]);

  ngOnInit() {
    this.isAuditHistoryLayout = this.isAuditHistoryPopup();
    this.columnMode = ColumnMode.force;
    this.pageSize = this.isAuditHistoryLayout ? 18 : undefined;
    this.rowHeight = this.isAuditHistoryLayout ? 42 : 'auto';
    this.projectTitle =
      String(this.data?.sourceRow?.title ?? this.data?.title ?? '').trim();

    const rawRequestPayload = this.data?.requestPayload;
    if (this.data?.useRawPayload === true && rawRequestPayload && this.data?.apiUrl) {
      this.isLoading = true;
      this.configService
        .customApi(this.data.apiUrl, rawRequestPayload, this.data, 'post')
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe({
          next: (res) => {
            let extractedRows = res?.responseList?.[0]?.responseData;
            if (!extractedRows) {
              const list0 = res?.responseList?.[0] || {};
              extractedRows = (list0['phe-summary']?.[0]?.records 
                || list0['phe-spend']?.[0]?.records 
                || list0[this.data?.responseKey]?.[0]?.records 
                || res) ?? [];
            }
            this.rows = this.normalizePopupRows(extractedRows);
            this.isLoading = false;
            this.triggerResize();
          },
          error: () => {
            this.isLoading = false;
          },
        });
      return;
    }

    const hasRemoteSource = !!this.data?.apiUrl && !!this.data?.functionName;
    const staticRows = this.data?.rows;
    if (Array.isArray(staticRows) && !hasRemoteSource) {
      this.rows = staticRows;
      this.isLoading = false;
      this.triggerResize();
      return;
    }

    if (!hasRemoteSource) {
      this.rows = [];
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.data.includeMasterFilter = true;
    this.configService
      .fetchData(
        this.data.apiUrl,
        this.data.functionName,
        this.data.filter,
        this.data
      )
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res) => {
          let extractedRows = res?.responseList?.[0]?.responseData;
          if (!extractedRows) {
            const list0 = res?.responseList?.[0] || {};
            extractedRows = list0['phe-summary']?.[0]?.records 
              || list0['phe-spend']?.[0]?.records 
              || list0[this.data?.responseKey]?.[0]?.records;
          }
          this.rows = this.normalizePopupRows(extractedRows);
          this.isLoading = false;
          this.triggerResize();
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private triggerResize() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);
  }

  private normalizePopupRows(rows: any): any {
    if (!this.isAuditHistoryPopup()) {
      return rows;
    }

    const historyRecords = this.extractHistoryRecords(rows);
    if (!historyRecords.length) {
      return [];
    }

    const sortedRecords = [...historyRecords].sort((a, b) => {
      const versionA = Number(a?.version_no ?? 0);
      const versionB = Number(b?.version_no ?? 0);
      if (versionA !== versionB) return versionA - versionB;
      return (
        new Date(a?.updated_date || a?.created_date || 0).getTime() -
        new Date(b?.updated_date || b?.created_date || 0).getTime()
      );
    });

    const mappedRows: any[] = [];
    // Build diffs from newest to oldest so latest changes appear at the top.
    for (let i = sortedRecords.length - 1; i >= 1; i--) {
      mappedRows.push(
        ...this.buildAuditDiffRows(sortedRecords[i - 1], sortedRecords[i])
      );
    }

    // Always include initial insert snapshot below diff blocks.
    mappedRows.push(...this.buildAuditSnapshotRows(sortedRecords[0]));

    if (mappedRows.length) {
      return mappedRows;
    }

    return [];
  }

  private isAuditHistoryPopup(): boolean {
    const headerKeys = (this.data?.headers || []).map((header: any) =>
      String(header?.key || '').trim()
    );
    return (
      this.data?.title === 'Audit History' &&
      headerKeys.includes('field') &&
      headerKeys.includes('oldValue') &&
      headerKeys.includes('newValue')
    );
  }

  private extractHistoryRecords(rows: any): any[] {
    if (Array.isArray(rows)) {
      if (rows.length && Array.isArray(rows[0]?.records)) {
        return rows[0].records;
      }
      return rows;
    }
    return [];
  }

  private buildAuditDiffRows(previous: any, current: any): any[] {
    const keys = new Set<string>([
      ...Object.keys(previous || {}),
      ...Object.keys(current || {}),
    ]);
    const auditRows: any[] = [];

    keys.forEach((key) => {
      if (this.auditMetaKeys.has(key)) return;
      if (!this.shouldIncludeAuditField(key)) return;

      const oldValue = this.formatAuditValue(previous?.[key]);
      const newValue = this.formatAuditValue(current?.[key]);
      if (oldValue === newValue) return;

      auditRows.push({
        field: this.getFieldDisplayName(key),
        oldValue,
        newValue,
        updatedBy: current?.updated_by || current?.created_by || '-',
        updatedOn: this.formatAuditDateTime(
          current?.updated_date || current?.created_date
        ),
      });
    });

    return auditRows;
  }

  private buildAuditSnapshotRows(record: any): any[] {
    return Object.keys(record || {})
      .filter((key) => !this.auditMetaKeys.has(key))
      .filter((key) => this.shouldIncludeAuditField(key))
      .map((key) => ({
        field: this.getFieldDisplayName(key),
        oldValue: '-',
        newValue: this.formatAuditValue(record?.[key]),
        updatedBy: record?.updated_by || record?.created_by || '-',
        updatedOn: this.formatAuditDateTime(
          record?.updated_date || record?.created_date
        ),
      }));
  }

  private shouldIncludeAuditField(key: string): boolean {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey) return false;

    const allowedAuditFields = this.getAllowedAuditFields();
    if (!allowedAuditFields.size) {
      return true;
    }

    return allowedAuditFields.has(normalizedKey);
  }

  private getAllowedAuditFields(): Set<string> {
    const configured = Array.isArray(this.data?.allowedAuditFields)
      ? this.data.allowedAuditFields
      : [];

    const normalized = configured
      .map((field: any) => String(field || '').trim())
      .filter((field: string) => !!field);

    return new Set(normalized);
  }

  private getFieldDisplayName(key: string): string {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey) return '-';

    const externalMap = this.data?.fieldLabelMap || {};
    const mappedValue = externalMap?.[normalizedKey];
    if (mappedValue) {
      return String(mappedValue);
    }

    const headerMatch = (this.data?.headers || []).find(
      (header: any) => String(header?.key || '').trim() === normalizedKey
    );
    if (headerMatch?.label) {
      return String(headerMatch.label);
    }

    return normalizedKey;
  }

  private formatAuditValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (Array.isArray(value)) {
      const values = value
        .map((item) => String(item ?? '').trim())
        .filter((item) => item.length > 0);
      return values.length ? values.join(', ') : '-';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  private formatAuditDateTime(value: any): string {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}`;
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
